import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Helmet } from 'react-helmet';

import withWidth from '@material-ui/core/withWidth';
import { withStyles } from '@material-ui/core/styles';
import { getFormValues } from 'redux-form';

import Layout from 'components/common/Layout';
import Progress from 'components/common/Progress';
import Filter from 'components/common/Filter';
import TabNavigation from 'components/common/TabNavigation';
import WorkflowStatusesTable from 'components/settings/WorkflowStatusesTable';
import WorkflowStatusCreateEditDialog from 'components/settings/WorkflowStatusCreateEditDialog';
import WorkflowStatusReassignDialog from 'components/settings/WorkflowStatusReassignDialog';
import Snackbar from 'components/common/Snackbar';
import Button from 'components/common/Button';

import {
  fetchWorkflowStatuses,
  clearWorkflowStatuses,
} from 'redux/modules/workflowStatuses';
import { addRouterParamsPageSettings } from 'redux/modules/routerParams';

import { getFilterChangeTypes } from 'utils/filterChangeTypes';
import objectDiff from 'utils/objectDiff';
import throttle from 'utils/throttle';

import { appTitle, titleSeparator } from 'config/helmetSettings';

const styles = theme => ({
  filter: {
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      left: -8,
      right: -8,
      bottom: -24,
      backgroundColor: theme.palette.grey[100],
      [theme.breakpoints.down('sm')]: { 
        bottom: -8,
      },
    },
  },
});

class WorkspaceWorkflowStatuses extends Component {
  constructor(props) {
    super(props);

    this.state = {
      createDialogOpen: false,
      snackbarOpen: false,
      snackbarMessage: '',
      reassignWorkflowStatus: null,
      reassignDialogOpen: false,
      mobileFilterExpanded: ['xs', 'sm'].indexOf(props.width) === -1,
    };

    this.onRequest = this.onRequest.bind(this);
    this.onFilterButtonClick = this.onFilterButtonClick.bind(this);
    this.onCreateDialogClose = this.onCreateDialogClose.bind(this);
    this.onWorkflowStatusDeleteFail = this.onWorkflowStatusDeleteFail.bind(this);
    this.onSnackbarClose = this.onSnackbarClose.bind(this);
    this.onWorkflowStatusReassign = this.onWorkflowStatusReassign.bind(this);
    this.onReassignDialogClose = this.onReassignDialogClose.bind(this);
    this.onMobileFilterBottonClick = this.onMobileFilterBottonClick.bind(this);

    this.onThrottledRequest = throttle(this.onRequest, 2000, false, true);
  }

  componentWillMount() {
    this.props.actions.clearWorkflowStatuses();
  }

  componentDidMount() {
    this.onRequest();
  }

  componentWillReceiveProps(nextProps) {
    const { filters, width } = this.props;
    const filtersChanged = nextProps.filters.Name !== filters.Name;
    if (filtersChanged) {
      this.onRequest();
    }

    if (nextProps.width !== width) {
      this.setState({
        mobileFilterExpanded: ['xs', 'sm'].indexOf(nextProps.width) === -1,
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return ((Object.keys(objectDiff(this.props, nextProps)).length +
      Object.keys(objectDiff(this.state, nextState)).length) > 0);
  }

  componentDidUpdate(prevProps) {
    const { filters } = this.props;
    const { throttledFiltersChanged, filtersChanged } = getFilterChangeTypes(
      prevProps.filters,
      filters,
      {
        textFilterKeys: ['Name'],
      },
    );

    if (throttledFiltersChanged) {
      this.onThrottledRequest();
    } else if (filtersChanged) {
      this.onRequest();
    }
  }

  onRequest() {
    const { actions, filters } = this.props;
    const requestFilters = filters && Object.keys(filters).length > 0
      ? Object.keys(filters).map(key => ({
        name: key,
        operation: 'contains',
        val: filters[key],
        case_sensitivity: false,
      }))
      : null;
    actions.fetchWorkflowStatuses({
      filters: requestFilters,
      limit: 'Un-limit',
      triggerLoading: true,
    });
  }

  onFilterButtonClick() {
    this.setState({ createDialogOpen: true });
  }

  onCreateDialogClose() {
    this.setState({ createDialogOpen: false });
  }

  onWorkflowStatusDeleteFail({ error, reassignWorkflowStatus }) {
    this.setState({
      snackbarOpen: error ? true : false,
      snackbarMessage: error ? error : '',
      ...error && error.indexOf('This status already used in Order Lines') !== -1
        ? { reassignWorkflowStatus } : { reassignWorkflowStatus: null },
    });
  }

  onSnackbarClose() {
    this.setState({ snackbarOpen: false });
  }

  onWorkflowStatusReassign() {
    this.setState({
      reassignDialogOpen: true,
      snackbarOpen: false,
    });
  }

  onReassignDialogClose() {
    this.setState({ reassignDialogOpen: false });
  }

  onMobileFilterBottonClick() {
    this.setState({ mobileFilterExpanded: !this.state.mobileFilterExpanded });
  }

  render() {
    const { workflowStatuses, loading, filters, width, classes } = this.props;
    const {
      createDialogOpen,
      reassignDialogOpen,
      snackbarOpen,
      snackbarMessage,
      reassignWorkflowStatus,
      mobileFilterExpanded,
    } = this.state;
    return (
      <Layout variant="settings">
        <Helmet>
          <title>
            {appTitle} {titleSeparator} Workflow Statuses
          </title>
        </Helmet>
        <TabNavigation
          items={[
            {
              text: 'Note Categories',
              href: '/settings/workspace/note-categories',
            },
            {
              text: 'Document Categories',
              href: '/settings/workspace/document-categories',
            },
            {
              text: 'Workflow Statuses',
              href: '/settings/workspace/workflow-statuses',
            },
            {
              text: 'Claim Statuses',
              href: '/settings/workspace/claim-statuses',
            },
            {
              text: 'Claim Codes',
              href: '/settings/workspace/claim-adjustment-codes',
            },
          ]}
        />
        <Filter
          className={classes.filter}
          form="workflowStatusesFilter"
          placeholder="Search Workflow Statuses"
          primaryButtonText="Add New"
          onPrimaryButtonClick={this.onFilterButtonClick}
          top={width === 'xs' || width === 'sm' ? 110 : 126}
          mobileFilterExpanded={mobileFilterExpanded}
          onMobileFilterBottonClick={this.onMobileFilterBottonClick}
          filters={filters}
          beforeTable
        />
        {loading > 0 &&
          <Progress fixed />
        }
        <WorkflowStatusesTable
          items={workflowStatuses}
          onChangePage={this.onChangePage}
          onChangeRowsPerPage={this.onChangeRowsPerPage}
          loading={loading}
          onWorkflowStatusDeleteFail={this.onWorkflowStatusDeleteFail}
          mobileFilterExpanded={mobileFilterExpanded}
        />
        <WorkflowStatusCreateEditDialog
          form="workflowStatusCreate"
          open={createDialogOpen}
          onClose={this.onCreateDialogClose}
          onSubmitSuccess={() => {
            this.onCreateDialogClose();
            this.onRequest();
          }}
        />
        <WorkflowStatusReassignDialog
          open={reassignDialogOpen}
          onClose={this.onReassignDialogClose}
          onSubmitSuccess={() => {
            this.onReassignDialogClose();
          }}
          form="reassignWorkflowStatus"
          initialValues={{
            'Old Workflow Status ID': reassignWorkflowStatus ? reassignWorkflowStatus.ID : null,
            'Old Workflow Status Name': reassignWorkflowStatus ? reassignWorkflowStatus.Name : null,
          }}
        />
        <Snackbar
          open={snackbarOpen}
          variant="warning"
          message={snackbarMessage}
          autoHideDuration={5000}
          resumeHideDuration={0}
          onClose={this.onSnackbarClose}
          {...reassignWorkflowStatus ? {
            action: (
              <Button
                variant="raised"
                color="primary"
                onClick={this.onWorkflowStatusReassign}
              >
                Reassign Status
              </Button>
            ),
          } : {}}
        />
      </Layout>
    );
  }
}

WorkspaceWorkflowStatuses.propTypes = {
  workflowStatuses: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  loading: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    Name: PropTypes.string,
  }).isRequired,
  actions: PropTypes.shape({
    fetchWorkflowStatuses: PropTypes.func.isRequired,
    clearWorkflowStatuses: PropTypes.func.isRequired,
    addRouterParamsPageSettings: PropTypes.func.isRequired,
  }).isRequired,
};

const mapStateToProps = (store, ownProps) => {
  const workflowStatusesFilterValues = getFormValues('workflowStatusesFilter')(store);
  return {
    workflowStatuses: store.workflowStatuses.data,
    filters: {
      ...workflowStatusesFilterValues
        && workflowStatusesFilterValues.Filter
        ? { Name: workflowStatusesFilterValues.Filter }
        : {},
    },
    loading: store.workflowStatuses.loading,
    ...store.routerParams.pageSettings[ownProps.location.pathname],
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchWorkflowStatuses,
    clearWorkflowStatuses,
    addRouterParamsPageSettings,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withWidth()(withStyles(styles)(WorkspaceWorkflowStatuses)),
);



// WEBPACK FOOTER //
// ./src/pages/settings/WorkspaceWorkflowStatuses.js